import type { RemoteBundleResponse } from '@yimoka/i18n';
import { createI18n } from '@yimoka/i18n/react';
import { useSyncExternalStore } from 'react';
import { Trans as ReactI18nextTrans } from 'react-i18next';

import { apiExecutor } from '@/http';
import { ENV } from '@/env';
import { getBundledResourcesForLang } from '@/locales/bundled-fallback';
import {
  DEFAULT_LANGUAGE,
  getLanguage,
  setLanguage,
  setLoadingLang,
  subscribeLanguage,
  SUPPORT_LANGUAGES,
} from '@/root';

const bundledLocales = {
  'zh-CN': getBundledResourcesForLang('zh'),
  'en-US': getBundledResourcesForLang('en'),
} as const;

function useCurrentLanguage() {
  return useSyncExternalStore(subscribeLanguage, getLanguage, getLanguage);
}

function parseRemoteData(data: unknown): unknown {
  if (typeof data !== 'string') {
    return data;
  }
  try {
    return JSON.parse(data) as unknown;
  } catch {
    return null;
  }
}

function toNamespaceBundle(data: unknown): RemoteBundleResponse['data'] {
  const parsedData = parseRemoteData(data);
  if (!parsedData || typeof parsedData !== 'object' || Array.isArray(parsedData)) {
    return null;
  }
  return parsedData as RemoteBundleResponse['data'];
}

export const {
  i18n,
  I18nRoot,
  t,
  useTranslation,
  useSchemaI18nKey,
} = createI18n({
  bundled: bundledLocales,
  defaultLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORT_LANGUAGES.map(item => item.value),
  useLang: useCurrentLanguage,
  fetchRemote: ENV.loadRemoteLang
    ? async (lang) => {
      const response = await apiExecutor({
        url: '/shared/frontend/portal/object/content',
        method: 'GET',
        params: {
          name: 'icua-i18n',
        },
        headers: {
          'x-md-global-language': lang,
        },
      });
      return {
        ...response,
        data: toNamespaceBundle(response.data),
      };
    }
    : undefined,
  storage: {
    get: key => Promise.resolve(typeof localStorage?.getItem === 'function' ? localStorage.getItem(key) : null),
    set: (key, value) => {
      if (typeof localStorage?.setItem === 'function') {
        localStorage.setItem(key, value);
      }
      return Promise.resolve();
    },
    prefix: 'icua:i18n:',
  },
  subscribeAppResume: (onResume) => {
    const handler = () => {
      if (document.visibilityState === 'visible') onResume();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  },
  onLoadingChange: setLoadingLang,
  onLangChange: setLanguage,
  debug: import.meta.env.DEV && import.meta.env.VITE_I18N_DEBUG === 'true',
});

export const Trans: typeof ReactI18nextTrans = ReactI18nextTrans;

export type TFunction<_Namespace extends string = string> = (key: string, options?: Record<string, unknown>) => string;
